import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { UserPlus, Loader2, CheckCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { PublicLayout } from "@/components/PublicLayout";
import { childrenAPI } from "@/lib/api";
import { toast } from "sonner";
import { parse, isValid } from "date-fns";

const studyShifts = [
  { value: "manha", label: "Manhã" },
  { value: "tarde", label: "Tarde" },
  { value: "noite", label: "Noite" },
  { value: "integral", label: "Integral" },
];

const schoolGrades = [
  "1º Ano", "2º Ano", "3º Ano", "4º Ano", "5º Ano",
  "6º Ano", "7º Ano", "8º Ano", "9º Ano",
  "1º Médio", "2º Médio", "3º Médio"
];

export default function RegisterChildPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [isLoading, setIsLoading] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [registeredChild, setRegisteredChild] = useState(null);
  const [birthDateInput, setBirthDateInput] = useState("");
  
  const [formData, setFormData] = useState({
    full_name: "",
    date_of_birth: "",
    phone: "",
    school_grade: "",
    guardian_phone: "",
    guardian_name: "",
    school_name: "",
    study_shift: "",
  });

  // Format date input as DD/MM/AAAA
  const handleDateChange = (e) => {
    let value = e.target.value.replace(/\D/g, ""); // Remove non-digits
    
    if (value.length > 8) value = value.slice(0, 8);
    
    // Add slashes automatically
    if (value.length >= 5) {
      value = value.slice(0, 2) + "/" + value.slice(2, 4) + "/" + value.slice(4);
    } else if (value.length >= 3) {
      value = value.slice(0, 2) + "/" + value.slice(2);
    }
    
    setBirthDateInput(value);
    
    // Update formData with API format if valid
    if (value.length === 10) {
      const apiFormat = convertToApiFormat(value);
      if (apiFormat) {
        setFormData({ ...formData, date_of_birth: apiFormat });
      }
    }
  };

  // Convert DD/MM/YYYY to YYYY-MM-DD for API
  const convertToApiFormat = (dateStr) => {
    if (!dateStr || dateStr.length !== 10) return null;
    const [day, month, year] = dateStr.split("/");
    return `${year}-${month}-${day}`;
  };

  // Convert YYYY-MM-DD to DD/MM/YYYY for display
  const convertToDisplayFormat = (dateStr) => {
    if (!dateStr || dateStr.length !== 10) return "";
    const [year, month, day] = dateStr.split("-");
    return `${day}/${month}/${year}`;
  };

  // Validate date
  const isValidDate = (dateStr) => {
    if (!dateStr || dateStr.length !== 10) return false;
    const apiFormat = convertToApiFormat(dateStr);
    if (!apiFormat) return false;
    const parsed = parse(apiFormat, "yyyy-MM-dd", new Date());
    return isValid(parsed) && parsed <= new Date();
  };

  // Pre-fill from verification page
  useEffect(() => {
    if (location.state) {
      if (location.state.childName) {
        setFormData(prev => ({ ...prev, full_name: location.state.childName }));
      }
      if (location.state.dateOfBirth) {
        setFormData(prev => ({ ...prev, date_of_birth: location.state.dateOfBirth }));
        setBirthDateInput(convertToDisplayFormat(location.state.dateOfBirth));
      }
    }
  }, [location.state]);

  const calculateAge = (birthDateStr) => {
    if (!birthDateStr) return "";
    const today = new Date();
    const birth = new Date(birthDateStr);
    let age = today.getFullYear() - birth.getFullYear();
    const m = today.getMonth() - birth.getMonth();
    if (m < 0 || (m === 0 && today.getDate() < birth.getDate())) {
      age--;
    }
    return age;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!isValidDate(birthDateInput)) {
      toast.error("Data de nascimento inválida. Use o formato DD/MM/AAAA");
      return;
    }

    const submitData = {
      ...formData,
      date_of_birth: convertToApiFormat(birthDateInput)
    };

    setIsLoading(true);

    try {
      const response = await childrenAPI.publicRegister(submitData);
      setRegisteredChild(response.data);
      setIsSuccess(true);
      toast.success("Cadastro realizado com sucesso!");
    } catch (error) {
      const message = error.response?.data?.detail || "Erro ao cadastrar";
      toast.error(message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleProceedToSchedule = () => {
    if (registeredChild) {
      navigate(`/agendar/${registeredChild.id}`);
    }
  };

  if (isSuccess && registeredChild) {
    return (
      <PublicLayout showBack backTo="/verificar">
        <div className="min-h-[calc(100vh-200px)] flex items-center justify-center py-12 px-4">
          <Card className="w-full max-w-md glass-card shadow-2xl animate-scale-in">
            <CardContent className="pt-8 text-center">
              <div className="w-20 h-20 rounded-full bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center mx-auto mb-6">
                <CheckCircle className="w-10 h-10 text-emerald-600 dark:text-emerald-400" />
              </div>
              <h2 className="font-nunito font-bold text-2xl mb-2">Cadastro Realizado!</h2>
              <p className="text-muted-foreground mb-6">
                {registeredChild.full_name} foi cadastrado(a) com sucesso.
              </p>
              <div className="p-4 rounded-xl bg-muted/50 mb-6 text-left">
                <p className="text-sm"><strong>Nome:</strong> {registeredChild.full_name}</p>
                <p className="text-sm"><strong>Idade:</strong> {registeredChild.age} anos</p>
                <p className="text-sm"><strong>Escola:</strong> {registeredChild.school_name}</p>
                <p className="text-sm"><strong>Responsável:</strong> {registeredChild.guardian_name}</p>
              </div>
              <Button
                onClick={handleProceedToSchedule}
                className="w-full rounded-full btn-animate"
                data-testid="proceed-to-workshop-button"
              >
                Escolher Workshop
              </Button>
            </CardContent>
          </Card>
        </div>
      </PublicLayout>
    );
  }

  return (
    <PublicLayout showBack backTo="/verificar">
      <div className="py-12 px-4">
        <div className="max-w-2xl mx-auto">
          <Card className="glass-card shadow-2xl animate-fade-in">
            <CardHeader className="text-center pb-2">
              <div className="w-16 h-16 rounded-2xl bg-primary/10 flex items-center justify-center mx-auto mb-4">
                <UserPlus className="w-8 h-8 text-primary" />
              </div>
              <CardTitle className="font-nunito text-2xl">Cadastrar Criança</CardTitle>
              <CardDescription>
                Preencha os dados para cadastrar a criança no sistema
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2 md:col-span-2">
                    <Label htmlFor="full_name">Nome Completo da Criança *</Label>
                    <Input
                      id="full_name"
                      value={formData.full_name}
                      onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
                      required
                      className="h-12 rounded-xl"
                      data-testid="register-fullname-input"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="date_of_birth">Data de Nascimento *</Label>
                    <Input
                      id="date_of_birth"
                      value={birthDateInput}
                      onChange={handleDateChange}
                      placeholder="DD/MM/AAAA"
                      inputMode="numeric"
                      maxLength={10}
                      required
                      className="h-12 rounded-xl"
                      data-testid="register-birthdate-input"
                    />
                    <p className="text-xs text-muted-foreground">
                      Digite: dia/mês/ano (ex: 10/05/2015)
                    </p>
                  </div>

                  <div className="space-y-2">
                    <Label>Idade</Label>
                    <Input
                      value={formData.date_of_birth ? `${calculateAge(formData.date_of_birth)} anos` : ""}
                      disabled
                      className="h-12 rounded-xl bg-muted"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="phone">Telefone da Criança</Label>
                    <Input
                      id="phone"
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      placeholder="(00) 00000-0000"
                      className="h-12 rounded-xl"
                      data-testid="register-phone-input"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label>Série Escolar *</Label>
                    <Select
                      value={formData.school_grade}
                      onValueChange={(value) => setFormData({ ...formData, school_grade: value })}
                      required
                    >
                      <SelectTrigger className="h-12 rounded-xl" data-testid="register-grade-select">
                        <SelectValue placeholder="Selecione a série" />
                      </SelectTrigger>
                      <SelectContent>
                        {schoolGrades.map((grade) => (
                          <SelectItem key={grade} value={grade}>{grade}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="school_name">Nome do Colégio *</Label>
                    <Input
                      id="school_name"
                      value={formData.school_name}
                      onChange={(e) => setFormData({ ...formData, school_name: e.target.value })}
                      required
                      className="h-12 rounded-xl"
                      data-testid="register-school-input"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label>Turno *</Label>
                    <Select
                      value={formData.study_shift}
                      onValueChange={(value) => setFormData({ ...formData, study_shift: value })}
                      required
                    >
                      <SelectTrigger className="h-12 rounded-xl" data-testid="register-shift-select">
                        <SelectValue placeholder="Selecione o turno" />
                      </SelectTrigger>
                      <SelectContent>
                        {studyShifts.map((shift) => (
                          <SelectItem key={shift.value} value={shift.value}>{shift.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="guardian_name">Nome do Responsável *</Label>
                    <Input
                      id="guardian_name"
                      value={formData.guardian_name}
                      onChange={(e) => setFormData({ ...formData, guardian_name: e.target.value })}
                      required
                      className="h-12 rounded-xl"
                      data-testid="register-guardian-name-input"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="guardian_phone">Telefone do Responsável *</Label>
                    <Input
                      id="guardian_phone"
                      value={formData.guardian_phone}
                      onChange={(e) => setFormData({ ...formData, guardian_phone: e.target.value })}
                      placeholder="(00) 00000-0000"
                      required
                      className="h-12 rounded-xl"
                      data-testid="register-guardian-phone-input"
                    />
                  </div>
                </div>

                <Button
                  type="submit"
                  className="w-full h-12 rounded-full btn-animate"
                  disabled={isLoading}
                  data-testid="register-submit-button"
                >
                  {isLoading ? (
                    <Loader2 className="w-5 h-5 animate-spin" />
                  ) : (
                    <>
                      <UserPlus className="w-5 h-5 mr-2" />
                      Cadastrar
                    </>
                  )}
                </Button>
              </form>
            </CardContent>
          </Card>
        </div>
      </div>
    </PublicLayout>
  );
}
